import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import './Cities.css';
import { db, hasFirebaseConfig } from '../lib/firebase';

const CITY_ICONS = ['🏛️', '🕌', '🏔️', '🌄', '🏙️', '🛣️'];
const AVG_CITY_TO_CITY_SPEED_KMH = 85;
const CITY_PRIORITY = ['islamabad', 'rawalpindi', 'lahore', 'peshawar'];

const Cities = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [errorText, setErrorText] = useState('');
  const [supportedCities, setSupportedCities] = useState([]);
  const [fareSettings, setFareSettings] = useState(null);

  useEffect(() => {
    if (!hasFirebaseConfig || !db) {
      setErrorText('Firebase is not configured for this website.');
      setIsLoading(false);
      return () => {};
    }

    let citiesReady = false;
    let fareReady = false;

    const markReady = () => {
      if (citiesReady && fareReady) {
        setIsLoading(false);
      }
    };

    const citiesQuery = query(collection(db, 'supported_cities'), where('active', '==', true));
    const unsubscribeCities = onSnapshot(
      citiesQuery,
      (snapshot) => {
        const cities = snapshot.docs
          .map((cityDoc) => {
            const data = cityDoc.data();
            return {
              id: cityDoc.id,
              name:
                typeof data.name === 'string' && data.name.trim()
                  ? data.name.trim()
                  : cityDoc.id,
              center:
                data.center &&
                typeof data.center.lat === 'number' &&
                typeof data.center.lng === 'number'
                  ? { lat: data.center.lat, lng: data.center.lng }
                  : null,
              services:
                data.services && typeof data.services === 'object'
                  ? data.services
                  : null,
            };
          })
          .filter((city) => city.services?.city !== false);

        setSupportedCities(sortCitiesByBusinessOrder(cities));
        setErrorText('');
        citiesReady = true;
        markReady();
      },
      (error) => {
        console.warn('Failed to load city master', error);
        setSupportedCities([]);
        setErrorText('Could not load city master from Firestore.');
        citiesReady = true;
        markReady();
      },
    );

    const unsubscribeFare = onSnapshot(
      doc(db, 'settings', 'fareSettings'),
      (snapshot) => {
        setFareSettings(snapshot.exists() ? snapshot.data() : null);
        fareReady = true;
        markReady();
      },
      (error) => {
        console.warn('Failed to load rate master', error);
        setFareSettings(null);
        setErrorText('Could not load rate master from Firestore.');
        fareReady = true;
        markReady();
      },
    );

    return () => {
      unsubscribeCities();
      unsubscribeFare();
    };
  }, []);

  const routes = useMemo(() => buildRoutePairs(supportedCities).slice(0, 24), [supportedCities]);

  const routesWithPricing = useMemo(() => {
    if (!fareSettings) return [];

    return routes.map((route) => {
      const price = computeCityComfortFare(route.distanceKm, fareSettings);
      return {
        ...route,
        distance: formatDistance(route.distanceKm),
        time: formatDuration(route.distanceKm),
        price: price !== null ? `Starting PKR ${formatCurrency(price)}` : 'Rate unavailable',
      };
    });
  }, [routes, fareSettings]);

  return (
    <div className="cities-page">
      <section className="cities-hero">
        <div className="container">
          <h1>Our Network</h1>
          <p>Connecting Pakistan's major cities with reliable, comfortable rides</p>
        </div>
      </section>

      <section className="cities-grid-section">
        <div className="container">
          <div className="city-cards">
            {supportedCities.map((city, index) => (
              <div className="city-info-card" key={city.id || city.name}>
                <div className="city-icon">{CITY_ICONS[index % CITY_ICONS.length]}</div>
                <h3>{city.name}</h3>
                <p>Active service area</p>
                <span className="city-badge">Active</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="routes-section">
        <div className="container">
          <h2 className="section-title">Popular Routes</h2>
          <p className="section-subtitle">Fixed pricing from rate master</p>

          <div className="routes-table">
            <div className="route-header">
              <span>Route</span>
              <span>Distance</span>
              <span>Duration</span>
              <span>Starting Price</span>
            </div>
            {routesWithPricing.map((route, index) => (
              <div className="route-row" key={`${route.from}-${route.to}-${index}`}>
                <span className="route-name">{route.from} ↔ {route.to}</span>
                <span>{route.distance}</span>
                <span>{route.time}</span>
                <span className="route-price">{route.price}</span>
              </div>
            ))}
          </div>

          {isLoading ? <p className="cities-note">Loading city and rate masters from Firestore...</p> : null}
          {!isLoading && !errorText && supportedCities.length === 0 ? (
            <p className="cities-note">No active cities found in city master.</p>
          ) : null}
          {!isLoading && !errorText && supportedCities.length > 0 && routesWithPricing.length === 0 ? (
            <p className="cities-note">Add city centers in city master to auto-generate route pricing.</p>
          ) : null}
          {errorText ? <p className="cities-note">{errorText}</p> : null}

          <div className="expansion-notice">
            <h3>🚀 Expanding Soon</h3>
            <p>
              We are rapidly expanding to connect more cities across Pakistan. Stay tuned for
              announcements about new launches.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

const getCityTierKey = (distanceKm) => {
  if (distanceKm < 100) return 'under100';
  if (distanceKm < 150) return 'between100To150';
  if (distanceKm < 200) return 'between150To200';
  if (distanceKm < 250) return 'between200To250';
  if (distanceKm < 300) return 'between250To300';
  if (distanceKm < 350) return 'between300To350';
  return 'over350';
};

const computeCityComfortFare = (distanceKm, settings) => {
  const city = settings?.cityToCity || {};
  const baseRate = Number(city.comfort);
  const minFare = Number(city.minFare);
  const tierRate = Number(city?.tiers?.[getCityTierKey(distanceKm)]?.comfort);
  const rate = Number.isFinite(tierRate) && tierRate > 0 ? tierRate : baseRate;

  if (!Number.isFinite(rate) || rate <= 0 || !Number.isFinite(minFare) || minFare < 0) {
    return null;
  }

  const rawFare = Math.max(distanceKm * rate, minFare);
  const roundedDownTo25 = Math.floor(rawFare / 25) * 25;
  return Math.max(roundedDownTo25, minFare);
};

const formatDistance = (distanceKm) => `${Math.round(distanceKm)} km`;

const formatDuration = (distanceKm) => {
  const totalHours = distanceKm / AVG_CITY_TO_CITY_SPEED_KMH;
  if (totalHours < 1) {
    return `${Math.max(1, Math.round(totalHours * 60))} mins`;
  }
  const rounded = Math.round(totalHours * 2) / 2;
  return `${rounded} hours`;
};

const formatCurrency = (value) => new Intl.NumberFormat('en-PK').format(value);

const toRadians = (value) => (value * Math.PI) / 180;

const haversineDistanceKm = (a, b) => {
  const earthRadiusKm = 6371;
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);
  const dLat = lat2 - lat1;
  const dLng = toRadians(b.lng - a.lng);
  const hav =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadiusKm * Math.asin(Math.sqrt(hav));
};

const normalizeCityName = (name) =>
  String(name || '')
    .toLowerCase()
    .replace(', pakistan', '')
    .trim();

const sortCitiesByBusinessOrder = (cities) =>
  [...cities].sort((a, b) => {
    const nameA = normalizeCityName(a.name);
    const nameB = normalizeCityName(b.name);
    const indexA = CITY_PRIORITY.indexOf(nameA);
    const indexB = CITY_PRIORITY.indexOf(nameB);

    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return nameA.localeCompare(nameB);
  });

const buildRoutePairs = (cities) => {
  const withCenter = sortCitiesByBusinessOrder(
    cities.filter((city) => city.center?.lat && city.center?.lng),
  );
  const pairs = [];

  for (let i = 0; i < withCenter.length; i += 1) {
    for (let j = i + 1; j < withCenter.length; j += 1) {
      const from = withCenter[i];
      const to = withCenter[j];
      if (isTwinCityPair(from.name, to.name)) {
        continue;
      }
      pairs.push({
        from: from.name,
        to: to.name,
        distanceKm: haversineDistanceKm(from.center, to.center),
      });
    }
  }

  return pairs;
};

const isTwinCityPair = (fromName, toName) => {
  const from = normalizeCityName(fromName);
  const to = normalizeCityName(toName);
  return (
    (from === 'islamabad' && to === 'rawalpindi') ||
    (from === 'rawalpindi' && to === 'islamabad')
  );
};

export default Cities;
